import React from 'react';

import { aiLanguageMessages } from '@atlaskit/editor-common/ai-messages';

import { TranslateIcon } from '../../../../icons/prompt/translate';
import type { ConvoAiFeatureId } from '../../../../utils/request/types';
import { createInsertBelow, createReplace } from '../../actions/create-actions';
import type { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItemFactoryOptions,
	EditorPluginAIConfigItemWithOptions,
	EditorPluginAIConfigItem,
} from '../../config-items';
import {
	getSortedSupportedTranslationLanguages,
	isRefinementSupport,
} from '../../utils/translation-utils';

const supportedTranslationLanguages = getSortedSupportedTranslationLanguages();

export const createTranslationConfigItems = (
	options?: EditorPluginAIConfigItemFactoryOptions,
): {
	[k: string]: EditorPluginAIConfigItem;
} => {
	const featureId: ConvoAiFeatureId = 'translate_adf_gpt41mini';

	const translationConfigItems = supportedTranslationLanguages
		.map((language) => {
			const lang = aiLanguageMessages[language];
			if (!lang) {
				return false;
			}
			const configItem: EditorPluginAIConfigItem = {
				key: `Translate to ${language}` as CONFIG_ITEM_KEYS,
				title: aiLanguageMessages[`${language}Title`],
				description: aiLanguageMessages[`${language}Description`],
				icon: () => <TranslateIcon />,
				showInRefineDropdown: isRefinementSupport(language),
				nestingConfig: {
					id: 'translate-to',
					parentTestId: 'translate-to',
					parentTitle: aiLanguageMessages.nestingParentTitlePostGA,
					shortTitle: lang,
					icon: () => <TranslateIcon />,
				},
				intentSchemaId: 'translate_intent_schema.json',
				featureId,
				adfPrompt: true,
				requiresTextualContent: true,
				lang: lang.defaultMessage,
				actions: {
					empty: [
						createInsertBelow({
							appearance: 'secondary',
							factoryOptions: options,
						}),
						createReplace({ appearance: 'primary' }),
					],
					selection: [
						createInsertBelow({
							appearance: 'secondary',
							factoryOptions: options,
						}),
						createReplace({ appearance: 'primary' }),
					],
				},
			};

			return [language, configItem];
		})
		.filter(Boolean) as [string, EditorPluginAIConfigItem][];

	return Object.fromEntries(translationConfigItems);
};

export const translationConfigItems: (
	options?: EditorPluginAIConfigItemFactoryOptions,
) => EditorPluginAIConfigItem[] = (options) => Object.values(createTranslationConfigItems(options));

export const translationConfigItemWithOptions: (
	options?: EditorPluginAIConfigItemFactoryOptions,
) => EditorPluginAIConfigItemWithOptions[] = (options) =>
	translationConfigItems(options).map((config) => ({
		config,
		triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
	}));

export const translationConfigItemWithOptionsEnabledForSelectionOnly: (
	options?: EditorPluginAIConfigItemFactoryOptions,
) => EditorPluginAIConfigItemWithOptions[] = (options) =>
	translationConfigItems(options).map((config) => ({
		config,
		triggers: { empty: { enabled: false }, selection: { enabled: true } },
	}));
